// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { app, authentication } from '@microsoft/teams-js';
import { storeCurrentRelativeUrl } from './storage';
import { createQueryUrl, QueryParams } from './apiClient.utils';
import { newUuid } from './uuid';

export type ErrorResponseHandler = (
    resp: Response,
    defaultHandler: (resp: Response) => Promise<boolean>
) => Promise<boolean>;

export type ErrorHandler = (
    error: unknown,
    defaultHandler: (error: unknown) => Promise<void>
) => Promise<void>;

interface ApiRequestParamValues {
    query?: QueryParams;
    skipErrorHandling: boolean;
    skipAuthHeader: boolean;
    errorResponseHandler: ErrorResponseHandler;
    errorHandler: ErrorHandler;
}

export type ApiRequestParams = Partial<ApiRequestParamValues>;

const defaultParams: Omit<ApiRequestParamValues, 'query'> = {
    skipErrorHandling: false,
    skipAuthHeader: false,
    errorResponseHandler: (resp, defaultHandler) => defaultHandler(resp),
    errorHandler: (error, defaultHandler) => defaultHandler(error),
} as const;

const defaultJsonHeaders: HeadersInit = {
    'Content-Type': 'application/json',
} as const;

export type ApiResponse<T = unknown> = {
    readonly data: T;
    readonly headers: Headers;
    readonly status: number;
};

export class ApiClient {
    private async invoke(
        url: string,
        init: RequestInit,
        params?: ApiRequestParams
    ): Promise<Response> {
        params = params ?? {};

        const skipErrorHandling =
            params.skipErrorHandling ?? defaultParams.skipErrorHandling;

        let errorHandled = false;

        try {
            const query = params.query;
            const effectiveUrl = query ? createQueryUrl(url, query) : url;

            const request = new Request(effectiveUrl, init);

            const skipAuthHeader =
                params.skipAuthHeader ?? defaultParams.skipAuthHeader;

            if (!skipAuthHeader) {
                const token = await this.getAuthToken();
                request.headers.set('Authorization', `Bearer ${token}`);
            }

            const resp = await fetch(request);

            if (resp.ok) {
                return resp;
            }

            let throwError = true;

            if (!skipErrorHandling) {
                const errorResponseHandler =
                    params.errorResponseHandler ??
                    defaultParams.errorResponseHandler;

                const responseValid = await errorResponseHandler(
                    resp,
                    (resp) => {
                        ApiClient.handleError(resp);
                        return Promise.resolve(false);
                    }
                );

                throwError = !responseValid;
                errorHandled = true;
            }

            if (throwError) {
                throw new Error(
                    `HTTP Error: ${resp.status} ${resp.statusText}`
                );
            }

            return resp;
        } catch (error) {
            if (!skipErrorHandling && !errorHandled) {
                const errorHandler =
                    params.errorHandler ?? defaultParams.errorHandler;

                await errorHandler(error, () => {
                    ApiClient.handleError();
                    return Promise.resolve();
                });
            }

            throw error;
        }
    }

    private async invokeJsonResponse<R = void>(
        url: string,
        init: RequestInit,
        params?: ApiRequestParams
    ): Promise<ApiResponse<R>> {
        const resp = await this.invoke(url, init, params);

        const text = await resp.text();
        const jsonData = text ? JSON.parse(text) : null;

        return {
            status: resp.status,
            data: jsonData as R,
            headers: resp.headers,
        };
    }

    public async get<R = void>(
        url: string,
        params?: ApiRequestParams
    ): Promise<ApiResponse<R>> {
        return await this.invokeJsonResponse(url, { method: 'GET' }, params);
    }

    public async getBlob(
        url: string,
        params?: ApiRequestParams
    ): Promise<ApiResponse<Blob>> {
        const resp = await this.invoke(url, { method: 'GET' }, params);

        const blobData = await resp.blob();

        return {
            status: resp.status,
            data: blobData,
            headers: resp.headers,
        };
    }

    public async delete<R = void>(
        url: string,
        params?: ApiRequestParams
    ): Promise<ApiResponse<R>> {
        return await this.invokeJsonResponse(url, { method: 'DELETE' }, params);
    }

    public async post<R = void, D = void>(
        url: string,
        data?: D,
        params?: ApiRequestParams
    ): Promise<ApiResponse<R>> {
        return await this.invokeJsonResponse(
            url,
            {
                method: 'POST',
                body: data !== undefined ? JSON.stringify(data) : undefined,
                headers: { ...defaultJsonHeaders },
            },
            params
        );
    }

    private static handleError(resp?: Response): void {
        if (resp) {
            const errorStatus = resp.status;

            if (errorStatus === 403) {
                window.location.href = `/errorpage/403`;
            } else if (errorStatus === 401) {
                window.location.href = `/errorpage/401`;
            } else {
                window.location.href = `/errorpage`;
            }
        } else {
            window.location.href = `/errorpage`;
        }
    }

    private async getAuthToken(): Promise<string> {
        if (!app.isInitialized()) {
            await app.initialize();
        }

        return new Promise((resolve, reject) => {
            const authTokenRequest = {
                successCallback: (token: string) => {
                    return resolve(token);
                },
                failureCallback: (error: string) => {
                    // When the getAuthToken function returns a "resourceRequiresConsent" error,
                    // it means Azure AD needs the user's consent before issuing a token to the app.
                    // The following code redirects the user to the "Sign in" page where the user can grant the consent.
                    // Right now, the app redirects to the consent page for any error.
                    console.error('Error from getAuthToken: ', error);
                    const state = newUuid();
                    storeCurrentRelativeUrl(state);
                    window.location.href = `/signin?state=${decodeURIComponent(state)}`;
                },
                resources: [],
            };

            authentication.getAuthToken(authTokenRequest);
        });
    }
}

const apiClient = new ApiClient();
export default apiClient;
